import React, { useState, useRef, useEffect } from 'react';
import { Lead, LeadStatus, LeadDetermination } from '../../types/lead.types';
import { SchoolSegment } from '../../types/project.types';
import { LeadSegmentPill } from './LeadSegmentPill';
import { getStatusColor } from '../../utils/leadColors';
import { getBusinessDaysRemaining } from '../../utils/dateUtils';
import {
  LEAD_STATUSES,
  LEAD_DETERMINATIONS,
  getStatusLabel,
  getDeterminationLabel,
} from '../../constants/leadConstants';
import { useContactLogger, ContactMethod } from '../../hooks/useContactLogger';
import { Phone, Mail, MessageSquare, User, MessageSquarePlus, X } from 'lucide-react';
import { format } from 'date-fns';

interface LeadTableProps {
  leads: Lead[];
  validationDeadlineDays?: number;
  onLeadSelect: (lead: Lead) => void;
  onLeadPatch?: (id: string, patch: Partial<Lead>) => Promise<void>;
  onContactLogged?: () => void;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleSelectAll?: () => void;
  segments?: SchoolSegment[];
}

const getParentName = (lead: Lead): string => {
  if (lead.parentInfo?.firstName || lead.parentInfo?.lastName) {
    return `${lead.parentInfo.firstName || ''} ${lead.parentInfo.lastName || ''}`.trim();
  }
  return lead.parentName || 'No Name';
};

const getEmail = (lead: Lead): string =>
  lead.parentInfo?.email || lead.email || '';

const getPhone = (lead: Lead): string =>
  lead.parentInfo?.phone || lead.phone || '';

const getLocation = (lead: Lead): string => {
  if (lead.city && lead.state) return `${lead.city}, ${lead.state}`;
  if (typeof lead.address === 'string' && lead.address) return lead.address;
  return '—';
};

// =========================================================================
// Inline-edit text cell. Click to edit, blur/Enter to commit, Esc to cancel.
// =========================================================================
interface InlineTextCellProps {
  value: string | number | undefined;
  onCommit: (next: string) => Promise<void> | void;
  type?: 'text' | 'tel' | 'email' | 'number';
  placeholder?: string;
  min?: number;
  max?: number;
  className?: string;
}

const InlineTextCell: React.FC<InlineTextCellProps> = ({
  value,
  onCommit,
  type = 'text',
  placeholder = '—',
  min,
  max,
  className = '',
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(value == null ? '' : String(value));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value == null ? '' : String(value));
  }, [value, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = async () => {
    if (saving) return;
    const trimmed = draft.trim();
    const original = value == null ? '' : String(value);
    if (trimmed === original) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onCommit(trimmed);
      setEditing(false);
    } catch {
      // Roll back to last known value on error.
      setDraft(original);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(value == null ? '' : String(value));
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type={type}
        value={draft}
        min={min}
        max={max}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') cancel();
        }}
        onClick={(e) => e.stopPropagation()}
        className={`w-full px-1 py-0.5 border border-indigo-400 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 ${className}`}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      className={`w-full text-left px-1 py-0.5 rounded text-sm hover:bg-indigo-50 hover:ring-1 hover:ring-indigo-200 truncate ${className}`}
      title="Click to edit"
    >
      {value == null || value === '' ? (
        <span className="text-gray-400">{placeholder}</span>
      ) : (
        String(value)
      )}
    </button>
  );
};

// =========================================================================
// Inline-edit dropdown cell. Used for status and determination.
// =========================================================================
interface InlineSelectCellProps<T extends string> {
  value: T | undefined;
  options: T[];
  labelOf: (v: T) => string;
  onCommit: (next: T) => Promise<void> | void;
  className?: string;
}

function InlineSelectCell<T extends string>({
  value,
  options,
  labelOf,
  onCommit,
  className = '',
}: InlineSelectCellProps<T>) {
  const [saving, setSaving] = useState(false);

  return (
    <select
      value={value || ''}
      disabled={saving}
      onClick={(e) => e.stopPropagation()}
      onChange={async (e) => {
        const next = e.target.value as T;
        if (next === value) return;
        setSaving(true);
        try {
          await onCommit(next);
        } finally {
          setSaving(false);
        }
      }}
      className={`px-1 py-0.5 text-xs rounded border border-transparent hover:border-indigo-200 focus:outline-none focus:ring-1 focus:ring-indigo-500 ${className}`}
    >
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {labelOf(opt)}
        </option>
      ))}
    </select>
  );
}

// =========================================================================
// Expandable contact-log sub-row. Shares useContactLogger with the modal.
// =========================================================================
interface ContactLogRowProps {
  lead: Lead;
  colSpan: number;
  onSaved: () => void;
  onCancel: () => void;
}

const ContactLogRow: React.FC<ContactLogRowProps> = ({
  lead,
  colSpan,
  onSaved,
  onCancel,
}) => {
  const logger = useContactLogger(lead, {
    onSaved: () => onSaved(),
  });

  const methods: { value: ContactMethod; label: string; icon: React.ReactNode }[] = [
    { value: 'phone', label: 'Phone', icon: <Phone className="w-3 h-3" /> },
    { value: 'email', label: 'Email', icon: <Mail className="w-3 h-3" /> },
    { value: 'text', label: 'Text', icon: <MessageSquare className="w-3 h-3" /> },
    { value: 'in-person', label: 'In Person', icon: <User className="w-3 h-3" /> },
  ];

  return (
    <tr className="bg-indigo-50">
      <td colSpan={colSpan} className="px-3 py-3">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-medium text-indigo-900">Log contact for {getParentName(lead)}:</span>
            <div className="flex items-center gap-1">
              {methods.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => logger.setMethod(m.value)}
                  className={`flex items-center gap-1 px-2 py-1 text-xs rounded border ${
                    logger.method === m.value
                      ? 'border-indigo-500 bg-indigo-100 text-indigo-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {m.icon}
                  {m.label}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-1 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={logger.successful}
                onChange={(e) => logger.setSuccessful(e.target.checked)}
                className="h-3 w-3 text-indigo-600"
              />
              Made contact
            </label>
          </div>
          <div className="flex items-start gap-2">
            <textarea
              value={logger.notes}
              onChange={(e) => logger.setNotes(e.target.value)}
              rows={2}
              placeholder="What happened on this contact attempt?"
              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  logger.save();
                }
              }}
            />
            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={async () => {
                  const ok = await logger.save();
                  if (ok) onCancel();
                }}
                disabled={logger.saving || !logger.notes.trim()}
                className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                {logger.saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="px-3 py-1 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
};

// =========================================================================
// Main table
// =========================================================================
const LeadTable: React.FC<LeadTableProps> = ({
  leads,
  validationDeadlineDays,
  onLeadSelect,
  onLeadPatch,
  onContactLogged,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  segments,
}) => {
  const [expandedLeadId, setExpandedLeadId] = useState<string | null>(null);

  const editingEnabled = !!onLeadPatch;
  const selectionEnabled = !!selectedIds && !!onToggleSelect;
  const showSchool = !!(segments && segments.length > 0);

  // Number of columns including selection (used for the expand row colspan).
  const totalCols = (selectionEnabled ? 1 : 0) + (showSchool ? 1 : 0) + 11 /* visible columns */;

  const allSelected =
    selectionEnabled &&
    leads.length > 0 &&
    leads.every((l) => selectedIds!.has(l.id));

  const patch = async (id: string, p: Partial<Lead>) => {
    if (!onLeadPatch) return;
    await onLeadPatch(id, p);
  };

  return (
    <div className="hidden md:block">
      <div className="overflow-auto shadow ring-1 ring-black ring-opacity-5 rounded-lg max-h-[calc(100vh-260px)]">
        <table className="min-w-[1200px] divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              {selectionEnabled && (
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={onToggleSelectAll}
                    className="h-4 w-4 text-indigo-600 rounded"
                  />
                </th>
              )}
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              {showSchool && (
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School</th>
              )}
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Determination</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days Left</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kids</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quality</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Contact</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Log</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {leads.map((lead) => {
              const isExpanded = expandedLeadId === lead.id;
              const isSelected = selectionEnabled && selectedIds!.has(lead.id);

              return (
                <React.Fragment key={lead.id}>
                  <tr
                    onClick={() => onLeadSelect(lead)}
                    className={`cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-indigo-50' : ''}`}
                  >
                    {selectionEnabled && (
                      <td className="px-3 py-2 w-8" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onToggleSelect!(lead.id)}
                          className="h-4 w-4 text-indigo-600 rounded"
                        />
                      </td>
                    )}

                    {/* Parent name */}
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 max-w-[180px] truncate">
                      {getParentName(lead)}
                    </td>

                    {/* School / campus */}
                    {showSchool && (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <LeadSegmentPill lead={lead} segments={segments} />
                      </td>
                    )}

                    {/* Status */}
                    <td className="px-3 py-2 whitespace-nowrap">
                      {editingEnabled ? (
                        <InlineSelectCell<LeadStatus>
                          value={(lead.status || 'uncontacted') as LeadStatus}
                          options={LEAD_STATUSES}
                          labelOf={getStatusLabel}
                          onCommit={(next) => patch(lead.id, { status: next })}
                          className={getStatusColor(lead.status || 'uncontacted')}
                        />
                      ) : (
                        <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(lead.status || 'uncontacted')}`}>
                          {getStatusLabel(lead.status || 'uncontacted')}
                        </span>
                      )}
                    </td>

                    {/* Determination */}
                    <td className="px-3 py-2 whitespace-nowrap">
                      {editingEnabled ? (
                        <InlineSelectCell<LeadDetermination>
                          value={(lead.determination || 'pending') as LeadDetermination}
                          options={LEAD_DETERMINATIONS}
                          labelOf={getDeterminationLabel}
                          onCommit={(next) => patch(lead.id, { determination: next })}
                        />
                      ) : (
                        <span className="text-xs">{getDeterminationLabel(lead.determination || 'pending')}</span>
                      )}
                    </td>

                    {/* Days left */}
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      {validationDeadlineDays && lead.determination === 'pending' && lead.status === 'uncontacted' && lead.createdAt ? (() => {
                        const { daysRemaining, isOverdue } = getBusinessDaysRemaining(
                          lead.createdAt.toDate(),
                          validationDeadlineDays
                        );
                        const color = isOverdue
                          ? 'text-red-600 font-semibold'
                          : daysRemaining <= 2
                          ? 'text-yellow-600 font-medium'
                          : 'text-green-600';
                        return <span className={color}>{isOverdue ? 'Overdue' : `${daysRemaining}d`}</span>;
                      })() : <span className="text-gray-300">—</span>}
                    </td>

                    {/* Phone */}
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 max-w-[140px]">
                      {editingEnabled ? (
                        <InlineTextCell
                          value={getPhone(lead)}
                          type="tel"
                          onCommit={(next) => patch(lead.id, { phone: next })}
                        />
                      ) : (
                        getPhone(lead) || '—'
                      )}
                    </td>

                    {/* Email */}
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 max-w-[200px]">
                      {editingEnabled ? (
                        <InlineTextCell
                          value={getEmail(lead)}
                          type="email"
                          onCommit={(next) => patch(lead.id, { email: next })}
                        />
                      ) : (
                        getEmail(lead) || '—'
                      )}
                    </td>

                    {/* Location */}
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 max-w-[160px] truncate">
                      {getLocation(lead)}
                    </td>

                    {/* Kids */}
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 text-center">
                      {lead.children?.length || 0}
                    </td>

                    {/* Quality */}
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 w-16">
                      {editingEnabled ? (
                        <InlineTextCell
                          value={lead.quality ?? ''}
                          type="number"
                          min={0}
                          max={10}
                          onCommit={(next) => {
                            const n = parseInt(next, 10);
                            return patch(lead.id, { quality: isNaN(n) ? 0 : Math.max(0, Math.min(10, n)) });
                          }}
                        />
                      ) : (
                        lead.quality ?? '—'
                      )}
                    </td>

                    {/* Last contacted */}
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                      {lead.lastContacted?.toDate
                        ? format(lead.lastContacted.toDate(), 'MMM d')
                        : <span className="text-gray-300">never</span>}
                    </td>

                    {/* Log button */}
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedLeadId(isExpanded ? null : lead.id);
                        }}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded border ${
                          isExpanded
                            ? 'bg-indigo-100 border-indigo-400 text-indigo-700'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                        title="Log a contact attempt"
                      >
                        {isExpanded ? <X className="w-3 h-3" /> : <MessageSquarePlus className="w-3 h-3" />}
                        {isExpanded ? 'Close' : 'Log'}
                      </button>
                    </td>
                  </tr>

                  {isExpanded && (
                    <ContactLogRow
                      lead={lead}
                      colSpan={totalCols}
                      onSaved={() => {
                        onContactLogged?.();
                      }}
                      onCancel={() => setExpandedLeadId(null)}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeadTable;
